import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost, apiPatch } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface LineItem {
  id: string;
  washType: string;
  retailPrice: number;
  createdAt: string;
  locationName: string;
  bayName: string;
}

interface InvoiceDetail {
  id: string;
  invoiceNumber: string;
  status: string;
  subtotal: number;
  discountPercentage: number | null;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  fleetAccountId: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  lineItems: Array<{
    transaction: {
      id: string;
      washType: string;
      retailPrice: number;
      createdAt: string;
    };
    vehicle: { licensePlate: string } | null;
    location: { name: string } | null;
    bay: { name: string } | null;
  }>;
  tierName: string | null;
}

interface PreviewResponse {
  lineItems: LineItem[];
  subtotal: number;
  discountPercentage: number | null;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  transactionCount: number;
  tierName: string | null;
}

/** EditInvoiceScreen - modify invoice line items, status, and totals. */
export default function EditInvoiceScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const { invoiceId } = useLocalSearchParams();
  const router = useRouter();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [allItems, setAllItems] = useState<LineItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState('pending');
  const [taxRate, setTaxRate] = useState(0);
  const [tierName, setTierName] = useState<string | null>(null);
  const [discountPercentage, setDiscountPercentage] = useState<number | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    loadData(ac.signal);
    return () => ac.abort();
  }, [invoiceId]);

  const loadData = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      // 1. Fetch invoice with linked line items
      const inv = await apiGet<InvoiceDetail>(`/api/fleet/invoices/${invoiceId}`);
      if (signal?.aborted) return;
      setInvoice(inv);
      setStatus(inv.status);
      setTierName(inv.tierName);
      setDiscountPercentage(inv.discountPercentage);

      // Calculate effective tax rate from invoice (tax is on subtotal minus discount)
      const netAmount = inv.subtotal - (inv.discountAmount || 0);
      const effectiveTaxRate = netAmount > 0
        ? Math.round((inv.taxAmount / netAmount) * 10000) / 100
        : 0;
      setTaxRate(effectiveTaxRate);

      // Map linked line items (pre-checked)
      const linkedItems: LineItem[] = inv.lineItems.map(li => ({
        id: li.transaction.id,
        washType: li.transaction.washType,
        retailPrice: li.transaction.retailPrice,
        createdAt: li.transaction.createdAt,
        locationName: li.location?.name || 'Unknown',
        bayName: li.bay?.name || '',
      }));

      const linkedIds = new Set(linkedItems.map(i => i.id));

      // 2. Fetch unbilled transactions for the same billing period
      let unbilledItems: LineItem[] = [];
      try {
        const preview = await apiPost<PreviewResponse>('/api/fleet/invoices/preview', {
          fleetAccountId: inv.fleetAccountId,
          billingPeriodStart: inv.billingPeriodStart,
          billingPeriodEnd: inv.billingPeriodEnd,
          taxRate: effectiveTaxRate,
        });
        if (signal?.aborted) return;
        unbilledItems = preview.lineItems.filter(i => !linkedIds.has(i.id));
      } catch {
        // No unbilled transactions — that's fine
      }

      if (signal?.aborted) return;
      // Merge: linked first, then unbilled
      const merged = [...linkedItems, ...unbilledItems];
      setAllItems(merged);
      setSelectedIds(new Set(linkedItems.map(i => i.id)));
    } catch (err: any) {
      if (!(signal?.aborted)) toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      if (!(signal?.aborted)) setLoading(false);
    }
  };

  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === allItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allItems.map(i => i.id)));
    }
  };

  const totals = useMemo(() => {
    const selected = allItems.filter(i => selectedIds.has(i.id));
    const taxDecimal = taxRate / 100;
    const subtotal = selected.reduce((s, t) => s + t.retailPrice, 0);
    const discountPct = discountPercentage || 0;
    const discountAmount = Math.round(subtotal * discountPct / 100);
    const taxAmount = Math.round((subtotal - discountAmount) * taxDecimal);
    const totalAmount = subtotal - discountAmount + taxAmount;
    return { subtotal, discountAmount, taxAmount, totalAmount, count: selected.length };
  }, [allItems, selectedIds, taxRate, discountPercentage]);

  const handleSave = async () => {
    if (selectedIds.size === 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Select at least one transaction' });
      return;
    }
    try {
      setSaving(true);
      await apiPatch(`/api/fleet/invoices/${invoiceId}`, {
        status,
        transactionIds: Array.from(selectedIds),
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        totalAmount: totals.totalAmount,
      });
      toast.show({ type: 'success', title: 'Saved', message: 'Invoice updated' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatWashType = (t: string) => {
    switch (t) {
      case 'self-serve': return 'Self-Serve';
      case 'automatic_basic': return 'Basic Wash';
      case 'automatic_premium': return 'Premium Wash';
      case 'automatic_deluxe': return 'Deluxe Wash';
      default: return t;
    }
  };

  const allSelected = allItems.length > 0 && selectedIds.size === allItems.length;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!invoice) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={{ fontSize: fontSize.md, color: colors.textSecondary }}>Invoice not found</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
      {/* Invoice info */}
      <Card style={{ marginBottom: spacing.md }}>
        <Text style={styles.invoiceNumber}>#{invoice.invoiceNumber}</Text>
        <Text style={styles.invoicePeriod}>
          {formatDate(invoice.billingPeriodStart)} - {formatDate(invoice.billingPeriodEnd)}
        </Text>
      </Card>

      {/* Status picker */}
      <Card style={{ marginBottom: spacing.md }}>
        <Text style={styles.sectionTitle}>Status</Text>
        <View style={styles.statusPicker}>
          {['pending', 'sent', 'overdue', 'cancelled'].map(s => (
            <TouchableOpacity
              key={s}
              onPress={() => setStatus(s)}
              style={[styles.statusChip, status === s && styles.statusChipActive]}
            >
              <Text style={[styles.statusChipText, status === s && styles.statusChipTextActive]}>
                {s}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </Card>

      {/* Line items with checkboxes */}
      {allItems.length > 0 && (
        <Card style={styles.previewCard}>
          <View style={styles.previewHeader}>
            <Text style={styles.previewTitle}>
              {totals.count} of {allItems.length} selected
            </Text>
            <TouchableOpacity onPress={toggleAll} style={styles.selectAllBtn}>
              <Ionicons
                name={allSelected ? 'checkbox' : 'square-outline'}
                size={20}
                color={colors.primary}
              />
              <Text style={styles.selectAllText}>{allSelected ? 'Deselect All' : 'Select All'}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.lineItemsHeader}>
            <View style={{ width: 32 }} />
            <Text style={[styles.lineItemHeaderText, { flex: 1 }]}>Description</Text>
            <Text style={[styles.lineItemHeaderText, { width: 80, textAlign: 'right' }]}>Amount</Text>
          </View>

          {allItems.map(item => {
            const checked = selectedIds.has(item.id);
            return (
              <TouchableOpacity key={item.id} style={styles.lineItem} onPress={() => toggleItem(item.id)} activeOpacity={0.6}>
                <View style={styles.checkboxWrap}>
                  <Ionicons
                    name={checked ? 'checkbox' : 'square-outline'}
                    size={22}
                    color={checked ? colors.primary : colors.textLight}
                  />
                </View>
                <View style={{ flex: 1, opacity: checked ? 1 : 0.4 }}>
                  <Text style={styles.lineItemDesc}>
                    {formatWashType(item.washType)}{item.bayName ? ` — ${item.bayName}` : ''}
                  </Text>
                  <Text style={styles.lineItemSub}>
                    {item.locationName} · {formatDate(item.createdAt)}
                  </Text>
                </View>
                <Text style={[styles.lineItemAmount, !checked && { opacity: 0.4 }]}>{fmt(item.retailPrice)}</Text>
              </TouchableOpacity>
            );
          })}

          {/* Totals section */}
          <View style={styles.totalsSection}>
            <View style={styles.totalRow}>
              <Text style={styles.subtotalLabel}>Subtotal</Text>
              <Text style={styles.subtotalValue}>{fmt(totals.subtotal)}</Text>
            </View>

            {totals.discountAmount > 0 && (
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, { color: colors.success }]}>
                  Fleet Discount{discountPercentage ? ` (${discountPercentage}%)` : ''}
                </Text>
                <Text style={[styles.totalValue, { color: colors.success }]}>-{fmt(totals.discountAmount)}</Text>
              </View>
            )}

            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Tax ({taxRate}%)</Text>
              <Text style={styles.totalValue}>{fmt(totals.taxAmount)}</Text>
            </View>

            <View style={[styles.totalRow, styles.grandTotalRow]}>
              <Text style={styles.grandTotalLabel}>Total Due</Text>
              <Text style={styles.grandTotalValue}>{fmt(totals.totalAmount)}</Text>
            </View>
          </View>
        </Card>
      )}

      {allItems.length === 0 && (
        <Card style={styles.emptyCard}>
          <Ionicons name="document-text-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyTitle}>No Transactions</Text>
          <Text style={styles.emptyText}>No transactions found for this invoice period.</Text>
        </Card>
      )}

      {/* Actions */}
      <View style={styles.actions}>
        <Button
          title="Cancel"
          onPress={() => router.back()}
          variant="outline"
          style={{ flex: 1 }}
        />
        <Button
          title={`Save${totals.count > 0 ? ` (${totals.count})` : ''}`}
          onPress={handleSave}
          loading={saving}
          disabled={totals.count === 0}
          icon={<Ionicons name="checkmark" size={20} color={colors.white} />}
          style={{ flex: 1 }}
        />
      </View>

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },

  invoiceNumber: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  invoicePeriod: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },

  sectionTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.md,
  },

  statusPicker: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  statusChip: {
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16,
    backgroundColor: c.surface, borderWidth: 1, borderColor: c.border,
  },
  statusChipActive: { backgroundColor: c.primary, borderColor: c.primary },
  statusChipText: { fontSize: fontSize.sm, color: c.textSecondary },
  statusChipTextActive: { color: c.white, fontWeight: fontWeight.semibold },

  previewCard: { marginBottom: spacing.md },
  previewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  previewTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.bold,
    color: c.text,
  },
  selectAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  selectAllText: {
    fontSize: fontSize.sm,
    color: c.primary,
    fontWeight: fontWeight.medium,
  },

  lineItemsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
    marginBottom: spacing.xs,
  },
  lineItemHeaderText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: c.textLight,
    textTransform: 'uppercase',
  },

  lineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  checkboxWrap: {
    width: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lineItemDesc: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  lineItemSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  lineItemAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, width: 80, textAlign: 'right' },

  totalsSection: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 2,
    borderTopColor: c.border,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
  },
  totalLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  totalValue: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },

  subtotalRow: {
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: c.borderLight,
    marginTop: spacing.xs,
  },
  subtotalLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  subtotalValue: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },

  grandTotalRow: {
    paddingTop: spacing.md,
    borderTopWidth: 2,
    borderTopColor: c.text,
    marginTop: spacing.sm,
  },
  grandTotalLabel: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  grandTotalValue: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.primary },

  emptyCard: { alignItems: 'center', paddingVertical: spacing.xxl },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs },

  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
